// @flow
import {
  call,
  put,
  race,
  select,
  takeEvery,
  all,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { normalize } from 'normalizr';
import i18n from 'i18n-js';

import {
  FETCH_ASSIGNED_STOPS_STARTED,
  FETCH_STUDENT_CHANGE_STOP_REQUESTS_STARTED,
  ADD_STUDENT_PARENT_STARTED,
  FETCH_STUDENT_GATEWAYS_STARTED,
  REMOVE_STUDENT_PARENT_STARTED,
  FETCH_STUDENT_PARENTS_STARTED,
  SAVE_STOP_ASSIGNATIONS_STARTED,
  STUDENT_FETCH_STARTED,
  UPDATE_STUDENT_STARTED,
} from '../types/students';
import { Students } from '../api';
import { REQUEST_TIMEOUT } from '../settings';
import * as selectors from '../reducers';
import * as studentActions from '../actions/students';
import * as parentActions from '../actions/parents';
import * as gatewaysUserActionsActions from '../actions/gatewayUserActions';
import * as gatewaysActions from '../actions/gateways';
import * as routeActions from '../actions/routes';
import * as closeableActions from '../actions/closeable';
import * as changeStopRequestsActions from '../actions/changeStopRequests';
import * as temporalBannersActions from '../actions/temporalBanners';
import * as messages from '../utils/errorDefaults';
import HTTP_STATUS from '../lib/HTTPStatus';
import { throwTimeout } from '../lib/http';
import { arrayOfRoutes } from '../api/schemas/routes';
import { arrayOfGatewayUserActions } from '../api/schemas/gatewayUserActions';
import { arrayOfRouteStops } from '../api/schemas/routeStops';
import { arrayOfUsers } from '../api/schemas/users';
import { arrayOfChangeStopRequest } from '../api/schemas/changeStopRequest';
import { arrayOfRouteStopSchedules } from '../api/schemas/routeStopSchedules';


function* fetchAssignedStops(action) {
  const studentId = parseInt(action.payload.id, 10);
  const token = yield select(selectors.getToken);

  try {
    // Server fetch
    const {
      responses: {
        routeStopsScheduleResponse,
        relatedRouteStopsResponse,
        relatedRoutesResponse,
      },
      timeout,
    } = yield race({
      responses: all({
        routeStopsScheduleResponse: call(
          [Students.custom, 'routeStopsSchedule'],
          { id: studentId, token },
        ),
        relatedRouteStopsResponse: call(
          [Students.custom, 'relatedRouteStops'],
          { id: studentId, token },
        ),
        relatedRoutesResponse: call(
          [Students.custom, 'relatedRoutes'],
          { id: studentId, token },
        ),
      }),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchAssignedStops saga');
    }

    // Register response
    const normalizedRoutes = normalize(
      relatedRoutesResponse,
      arrayOfRoutes,
    );
    const normalizedStops = normalize(
      relatedRouteStopsResponse,
      arrayOfRouteStops,
    );
    const normalizedSchedule = normalize(
      routeStopsScheduleResponse,
      arrayOfRouteStopSchedules,
    );

    yield put(routeActions.fetchRoutesSucceed(
      normalizedRoutes.entities.routes,
      normalizedRoutes.result,
    ));

    yield put(routeActions.fetchRouteStopsSucceed(
      -1,
      normalizedStops.entities.routeStops,
      normalizedStops.result,
    ));

    // Build key => week_day schedule
    // const scheduleBase = normalizedSchedule.entities.routeStopSchedule;
    // const routeStopsSchedule = {};
    // Object.keys(normalizedSchedule.entities.routeStopSchedule).forEach((key) => {
    //   routeStopsSchedule[key] = scheduleBase[key].week_days;
    // });
    yield put(studentActions.fetchAssignedStopSuccess({
      id: studentId,
      weekRouteStopSchedule: normalizedSchedule.entities.routeStopSchedule,
    }));
  } catch (error) {
    // Set error
    const {
      statusCode, message, data, isPlain,
    } = error;
    yield put(studentActions.fetchAssignedStopFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
    }));
  }
}

function* fetchAssignedGateways(action) {
  const studentId = parseInt(action.payload, 10);
  const token = yield select(selectors.getToken);

  try {
    // Server fetch
    const { studentGatewaysResponse, timeout } = yield race({
      studentGatewaysResponse: call(
        [Students.custom, 'gatewaysSchedule'],
        { token, id: studentId },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchStudentGateways saga');
    }

    // Register response
    const normalizedGatewayUserActions = normalize(
      studentGatewaysResponse,
      arrayOfGatewayUserActions,
    );
    const { gateways } = normalizedGatewayUserActions.entities;


    yield put(gatewaysActions.gatewaysFetchSucceed(
      gateways,
      gateways ? Object.keys(normalizedGatewayUserActions.entities.gateways) : [],
    ));

    yield put(gatewaysUserActionsActions.gatewaysUserActionFetchSucceed(
      normalizedGatewayUserActions.entities.gatewayUserAction,
      normalizedGatewayUserActions.result,
    ));
    yield put(studentActions.fetchStudentGatewayUserActionsSuccess(
      {
        id: studentId,
        gatewayUserActions: normalizedGatewayUserActions.result,
      },
    ));
  } catch (error) {
    // Set error
    const {
      statusCode, message, data, isPlain,
    } = error;
    yield put(studentActions.fetchStudentGatewaysFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
    }));
  }
}

function* saveAssignedStops(action) {
  const { studentId, stopsSchedule = {}, gatewaysSchedule } = action.payload;
  const token = yield select(selectors.getToken);
  const currentStopId = yield select(selectors.getCurrentRouteStopId);

  try {
    const { timeout } = yield race({
      studentResponse: call([Students.custom, 'updateRouteStopsSchedule'], {
        id: studentId,
        token,
        data: Object.keys(stopsSchedule).map(stopId => stopsSchedule[stopId]),
      }),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('saveAssignedStops saga');
    }

    // Artificial delay, to feel that is doing something
    yield call(delay, 1000);
    // Now save gateways
    const { timeout2, studentGatewaysResponse } = yield race({
      studentGatewaysResponse: call([Students.custom, 'updateGatewaysSchedule'], {
        id: studentId,
        token,
        data: Object.keys(gatewaysSchedule).map(stopId => (
          {
            ...gatewaysSchedule[stopId],
            id: gatewaysSchedule[stopId].gatewayUserAction,

          })),
      }),
      timeout2: call(delay, REQUEST_TIMEOUT),
    });


    yield put(studentActions.saveStopAssignationSuccess());
    yield put(closeableActions.close(`routesCalendar${studentId}`));
    // Fetch Stop again to update removed students.
    yield put(routeActions.fetchRouteStopStarted(currentStopId));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      isPlain,
    } = error;
    yield put(studentActions.saveStopAssignationFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : error.data,
    }));
  }
}

function* fetchStudent(action) {
  const { payload } = action;
  // const { page } = payload;
  const token = yield select(selectors.getToken);

  try {
    const { studentResponse, timeout } = yield race({
      studentResponse: call(
        [Students, 'detail'],
        { token, id: payload },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchParent saga');
    }

    yield put(studentActions.fetchStudentSucceed(studentResponse));
  } catch (error) {
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(studentActions.fetchStudentFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}


function* fetchStudentParents(action) {
  const { payload } = action;
  // const { page } = payload;
  const token = yield select(selectors.getToken);
  try {
    const { studentParentsResponse, timeout } = yield race({
      studentParentsResponse: call(
        [Students.custom, 'parents'],
        { token, id: payload },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchParent saga');
    }
    const { entities, result } = normalize(
      studentParentsResponse,
      arrayOfUsers,
    );
    yield put(studentActions.fetchStudentParentsSucceed(entities.users, result));
  } catch (error) {
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(studentActions.fetchStudentParentsFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* addStudentParent(action) {
  const { payload } = action;
  const { idStudent, idParent } = payload;
  const token = yield select(selectors.getToken);

  let student = yield select(selectors.getStudent, idStudent);

  try {
    const { studentParentsResponse, timeout } = yield race({
      studentParentsResponse: call(
        [Students.custom, 'addParent'],
        { token, id: idStudent, data: { parent: idParent } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchParent saga');
    }

    const { entities, result } = normalize(
      studentParentsResponse,
      arrayOfUsers,
    );

    let parentIds = [];

    if (student != null) {
      parentIds = student.parents || [];
    } else {
      yield put(studentActions.fetchStudentStarted(idStudent));
    }

    if (parentIds.filter(elm => elm === idParent).length === 0) {
      parentIds.push(idParent);
    }

    yield put(parentActions.fetchParentSucceed(studentParentsResponse));
    yield put(studentActions.addStudentParentSuccess(idStudent, parentIds));

  } catch (error) {
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(studentActions.fetchStudentParentsFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* removeParent(action) {
  const { payload } = action;
  const { idStudent, idParent } = payload;
  const token = yield select(selectors.getToken);
  const student = yield select(selectors.getStudent, idStudent);
  try {
    const { studentParentsResponse, timeout } = yield race({
      studentParentsResponse: call(
        [Students.custom, 'removeParent'],
        { token, id: idStudent, data: { parent: idParent } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('removeStudentParent saga');
    }
    const { entities, result } = normalize(
      studentParentsResponse,
      arrayOfUsers,
    );

    let parentIds = [];
    if (student.parents) {
      parentIds = student.parents;
      if (parentIds.filter(elm => elm === idParent).length > 0) {
        parentIds = parentIds.filter(elm => elm !== idParent);
      }
    }
    yield put(studentActions.removeStudentParentSuccess(idStudent, parentIds));
  } catch (error) {
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(studentActions.fetchStudentParentsFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* fetchStudentChangeStopRequests(action) {
  const { payload } = action;
  const { id } = payload;
  const token = yield select(selectors.getToken);
  try {
    const { studentChangeStopRequestResponse, timeout } = yield race({
      studentChangeStopRequestResponse: call(
        [Students.custom, 'changeRequests'],
        { token, id },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchParent saga');
    }
    const proccessedResponse = studentChangeStopRequestResponse.map(elm => ({ id: `${elm.instance_type}_${elm.id}`, obj_id: elm.id, ...elm }));
    const { entities, result } = normalize(
      proccessedResponse,
      arrayOfChangeStopRequest,
    );

    yield put(changeStopRequestsActions.fetchChangeStopRequestSucceed(
      entities.changeStopRequest,
      result,
    ));

    yield put(
      gatewaysActions.gatewaysFetchSucceed(
        entities.gateways,
        Object.keys(entities.gateways).map(elm => elm.parseInt()),
      ),
    );

    yield put(studentActions.addStudentChangeStopRequests(id,
      proccessedResponse.map(obj => obj.id)));
  } catch (error) {
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(studentActions.fetchChangeStopRequestFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}

function* updateStudent(action) {
  const token = yield select(selectors.getToken);
  const { id } = action.payload;
  const newStudent = {
    ...action.payload,
    profile_picture: undefined,
  };

  try {
    if (action.payload.phone) {
      newStudent["phone"] = `+502${action.payload.phone}`;
    };

    // Update  route
    const { studentResponse, timeout } = yield race({
      studentResponse: call(
        [Students, 'update'],
        {
          token,
          id,
          data: { ...newStudent },
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('updateStudent saga');
    }
    yield put(temporalBannersActions.shout(messages.DEFAULT_UPDATE(i18n.t('labels.students', { count: 1 })), 3000, 1, {}));
    yield put(studentActions.updateStudentSuccess({
      ...studentResponse,
    }));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(temporalBannersActions.shout(messages.UPDATE_ERROR(i18n.t('labels.students', { count: 1 })), messages.DEFAULT_DURATION, -1, {}));
    // yield put(userActions.updateUserSucceed(oldUser));
    yield put(studentActions.updateStudentFailed({
      statusCode,
      message: statusCode === HTTP_STATUS.NOT_FOUND ? 'Error al update de user' : message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}


export function* watchStudentFetch(): Iterator<any> {
  yield takeEvery(
    STUDENT_FETCH_STARTED,
    fetchStudent,
  );
}

export function* watchAssignedStops(): Iterator<any> {
  yield takeEvery(
    FETCH_ASSIGNED_STOPS_STARTED,
    fetchAssignedStops,
  );
}

export function* watchSaveAssignedStops(): Iterator<any> {
  yield takeEvery(
    SAVE_STOP_ASSIGNATIONS_STARTED,
    saveAssignedStops,
  );
}

export function* watchFetchStudentParents(): Iterator<any> {
  yield takeEvery(
    FETCH_STUDENT_PARENTS_STARTED,
    fetchStudentParents,
  );
}

export function* watchFetchStudentChangeStopRequests(): Iterator<any> {
  yield takeEvery(
    FETCH_STUDENT_CHANGE_STOP_REQUESTS_STARTED,
    fetchStudentChangeStopRequests,
  );
}


export function* watchFetchStudentGateways(): Iterator<any> {
  yield takeEvery(
    FETCH_STUDENT_GATEWAYS_STARTED,
    fetchAssignedGateways,
  );
}


export function* watchaddStudentParents(): Iterator<any> {
  yield takeEvery(
    ADD_STUDENT_PARENT_STARTED,
    addStudentParent,
  );
}


export function* watchRemoveStudentParents(): Iterator<any> {
  yield takeEvery(
    REMOVE_STUDENT_PARENT_STARTED,
    removeParent,
  );
}

export function* watchUpdateStudent(): Iterator<any> {
  yield takeEvery(
    UPDATE_STUDENT_STARTED,
    updateStudent,
  );
}



// WEBPACK FOOTER //
// ./src/scripts/sagas/students.js